// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@conklin/XDL/constants";
import { Divider } from "@conklin/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Changes
    topSection?: InjectableCss;
    topLeftSection?: InjectableCss;
    topRightSection?: InjectableCss;
    awards?: InjectableCss;
    awardsImages?: InjectableCss;
    socialLinks?: InjectableCss;
    locationsSection?: InjectableCss;
    botttomSection?: InjectableCss;
    copyrightText?: TypographyPresentationProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));

    // XNG-Changes
    const { isCheckoutPage, chatUrl, isAuthenticated } = useAppSelector(state => {
        return {
            isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
            chatUrl: getSession(state).properties?.chatUrl || "",
            isAuthenticated: xIsAuthenticated(state),
        };
    });

    React.useEffect(() => {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = chatUrl;
        script.async = true;
        document?.body?.appendChild(script);
    }, []);

    return (
        <StyledWrapper {...styles.widgetGridContainer} {...{ isCheckoutPage }}>
            {!isCheckoutPage && (
                <>
                    <StyledWrapper {...styles.topSection}>
                        <StyledWrapper {...styles.topLeftSection}>
                            <StyledWrapper {...styles.linkList1GridItem}>
                                <Zone zoneName="LinkList1" contentId={id} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList2GridItem}>
                                <Zone zoneName="LinkList2" contentId={id} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList3GridItem}>
                                <Zone zoneName="LinkList3" contentId={id} />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.topRightSection}>
                            <StyledWrapper {...styles.awards}>
                                <Zone zoneName="aboutConklin" contentId={id} />
                                <Divider margin="12px 0" borderColor="common.ui5" />
                                <StyledWrapper {...styles.awardsImages}>
                                    <XngZone zoneName="awards" contentId={id} />
                                </StyledWrapper>
                            </StyledWrapper>
                            {/* XNG-Changes: isAuthenticated */}
                            <StyledWrapper {...styles.socialLinks} {...{ isAuthenticated }}>
                                <Zone zoneName="SocialLinks" contentId={id} />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.locationsSection}>
                        <Zone zoneName="locations" contentId={id} />
                    </StyledWrapper>
                </>
            )}
            <StyledWrapper {...styles.botttomSection}>
                <Typography {...styles.copyrightText}>
                    {siteMessage(`Custom_Footer_Copyright`, String(new Date().getFullYear()))}
                </Typography>
                <Zone zoneName="SiteMapLinks" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
